import React from "react";
import { Link } from "react-router-dom";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Common after a fresh deploy: an old tab tries to import a chunk whose
    // hash no longer exists on the server. Force one auto-reload so the user
    // gets the new bundle instead of an angry error screen. We only auto-reload
    // ONCE per session (sessionStorage guard) to avoid infinite loops.
    const msg = String(error?.name || "") + " " + String(error?.message || "");
    const isChunkFailure =
      /ChunkLoadError|Loading chunk .* failed|Loading CSS chunk|Failed to fetch dynamically imported module/i.test(msg);
    if (isChunkFailure) {
      try {
        const key = "rl_chunk_reload_v1";
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          // eslint-disable-next-line no-console
          console.warn("Royal Lady: chunk load failure — auto-reloading once", error);
          window.location.reload();
          return;
        }
      } catch { /* sessionStorage may be blocked; fall through to the fallback UI */ }
    }
    // eslint-disable-next-line no-console
    console.error("Royal Lady ErrorBoundary caught:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-white" data-testid="app-error-boundary">
          <div className="mx-auto max-w-md px-6 py-20 text-center">
            <div className="text-[10px] uppercase tracking-[0.35em] text-[#B8912B]">
              Something went wrong
            </div>
            <h1 className="mt-2 font-heading text-2xl">
              We hit a small snag
            </h1>
            <p className="mt-2 text-sm text-gray-500">
              Please try again in a moment. Your cart and account are safe.
            </p>
            <div className="mt-6 flex flex-col gap-2">
              <button
                data-testid="error-retry"
                onClick={() => {
                  this.setState({ hasError: false, error: null });
                  window.location.reload();
                }}
                className="bg-black py-3 text-[11px] font-semibold uppercase tracking-widest text-white"
              >
                Reload page
              </button>
              <Link
                to="/"
                data-testid="error-home"
                onClick={() => this.setState({ hasError: false, error: null })}
                className="border border-black py-3 text-[11px] font-semibold uppercase tracking-widest text-black"
              >
                Back to home
              </Link>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
